import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useProductFilters } from '../hooks/useProductFilters';
import { decodeFilters } from '../hooks/filterUrl';
import Filters from '../components/Filters';
import Product from '../components/Product';
import ModelProduct from '../components/ModelProduct';
import Sort from '../components/Sort';
import { useProducts } from '../hooks/useProducts';
import { useProductModels } from '../hooks/useProductModels';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import { panelFiltersSchema, inverterFiltersSchema, magazineFiltersSchema, wallboxFiltersSchema } from '../filtersSchema';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen } from '@fortawesome/free-solid-svg-icons';
import { XMarkIcon, TagIcon } from '@heroicons/react/24/outline';
import { ADRES_OKAZJI, OKAZJE_DOSTEPNE } from '../config/okazje';
import BreadcrumbsComponent from '../components/Breadcrumbs';
import Loader from '../components/Loader';
import { fetchProducts } from '../requests';
import { getRecommendedIds, isRecommended } from '../config/recommendedProducts';
import { normalizeSearchQuery } from '../searchNormalize';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useTranslation } from 'react-i18next';
import { kluczKraju } from '../kraj';

// Tytuł karty: lista:kategoria.<productType>.
const KATEGORIE = ['panel', 'inwerter', 'magazyn', 'wallbox'];

const SELECTIVE_KEYS = [
  'manufacturer', 'types', 'phase', 'mppt', 'voltage', 'seller', 'frame_colour',
  'min_efficiency', 'min_product_warranty', 'module_length_min', 'module_length_max',
  'module_width_range', 'module_thickness_range', 'max_weight', 'min_price_per_wp',
  'max_price_per_wp', 'min_warranty',
];

// Nazwy wyświetla Sort z lista:sortowanie.<value>; `name` to polski zapas.
const MODEL_SORT_OPTIONS = [
  { name: 'Liczba ofert', value: 'offers' },
  { name: 'Dostępności', value: 'availability' },
  { name: 'Cena: rosnąco', value: 'asc' },
  { name: 'Cena: malejąco', value: 'desc' },
];

const ViewSwitch = ({ modelMode, onChange, t }) => (
  <div className="inline-flex rounded-md border border-gray-200 overflow-hidden shadow-sm">
    <button onClick={() => onChange('grouped')} className={`px-4 py-1.5 text-sm font-semibold transition-colors ${modelMode ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{t('widok.grupuj')}</button>
    <button onClick={() => onChange('offers')} className={`px-4 py-1.5 text-sm font-semibold border-l border-gray-200 transition-colors ${!modelMode ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{t('widok.wszystkie')}</button>
  </div>
);

/**
 * „Najlepsze okazje” obok przełącznika widoku, w tej samej kategorii co lista.
 * Przeniesione ze strony głównej (decyzja właściciela 2026-10-01): okazje są
 * przeglądaniem ofert konkretnej kategorii, więc ich miejsce jest przy liście,
 * a nie w hero. Żółta metka jak na dawnym pasku, żeby wejście było rozpoznawalne.
 * Strona okazji zna tylko moduły, falowniki i magazyny, więc przy ładowarkach
 * przycisku nie ma.
 */
const KATEGORIE_OKAZJI = ['panel', 'inwerter', 'magazyn'];

const PrzyciskOkazji = ({ productType, t }) => (
  <Link
    to={`${ADRES_OKAZJI}?kategoria=${productType}`}
    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md bg-blue-600 text-white text-sm font-semibold
               shadow-sm hover:bg-blue-500 active:bg-blue-700 transition-colors whitespace-nowrap"
  >
    <TagIcon className="w-4 h-4 text-yellow-300" />
    {t('najlepsze_okazje')}
  </Link>
);

const ProductPage = ({ productType, akcja = null }) => {
  const { t } = useTranslation('lista');
  useDocumentTitle(KATEGORIE.includes(productType) ? t(`kategoria.${productType}`) : undefined);

  // Widok listy zapamiętany osobno dla każdej wersji krajowej (Polska bez prefiksu).
  const viewKey = kluczKraju(`vv_view_${productType}`);
  const initialView = (() => {
    try { return localStorage.getItem(viewKey) || 'grouped'; } catch (e) { return 'grouped'; }
  })();

  // Filtry odtwarzane z URL (param `f`) — mount-only, żeby „Powrót" z produktu je zachował.
  const [initialApplied] = useState(() => {
    const f = new URLSearchParams(window.location.search).get('f');
    return decodeFilters(f) || { available_only: true };
  });

  const { filters, sortOrder, pageIndex, setFilters, setSortOrder, onPageChange } =
    useProductFilters(initialApplied, initialView === 'grouped' ? 'offers' : 'availability');
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);

  const [viewMode, setViewMode] = useState(initialView);
  const modelMode = viewMode === 'grouped';

  const pageSize = 25;

  const urlSearch = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  }, [location.search]);
  // Zapytanie wysyłane do API — z lekką normalizacją aliasów (sklejone/rozdzielone marki,
  // synonimy typu). `urlSearch` (surowe, jak wpisał użytkownik) zostaje bez zmian do wyświetlania.
  const apiSearch = useMemo(() => normalizeSearchQuery(urlSearch), [urlSearch]);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const { products, totalPages, totalCount, isLoading: isProductsLoading } = useProducts(
    productType, pageIndex, pageSize, filters, sortOrder, apiSearch, !modelMode
  );
  const { models, totalPages: modelTotalPages, totalCount: modelTotalCount, isLoading: isModelsLoading } =
    useProductModels(productType, pageIndex, pageSize, filters, sortOrder, apiSearch, modelMode);

  const [recommendedProducts, setRecommendedProducts] = useState([]);
  useEffect(() => {
    const ids = getRecommendedIds(productType);
    if (!ids.length) { setRecommendedProducts([]); return undefined; }
    let cancelled = false;
      const ep = productType === 'panel' ? 'get_panel'
      : productType === 'inwerter' ? 'get_inverter'
      : productType === 'wallbox' ? 'get_wallbox'
      : 'get_magazine';
    Promise.all(ids.map((id) => fetchProducts(`${ep}/${id}`).catch(() => null)))
      .then((results) => { if (!cancelled) setRecommendedProducts(results.filter((d) => d && !d.error && d.id != null)); });
    return () => { cancelled = true; };
  }, [productType]);

  // Filtry zakresowe (suwaki moc/cena/pojemność) mają domyślne wartości, więc nie są
  // w SELECTIVE_KEYS. Traktuj je jako aktywne dopiero, gdy użytkownik zawęził zakres
  // (min > 0 lub max < wartość domyślna) — inaczej przypięte "Polecane" pokazują się
  // wbrew filtrowi (np. moduł 615/650 W przy filtrze Moc od 700).
  const priceMaxDefault = productType === 'panel' ? 999 : 49999;
  const rangeNarrowed = (arr, defMin, defMax) =>
    Array.isArray(arr) &&
    ((arr[0] != null && arr[0] !== '' && Number(arr[0]) > defMin) ||
     (arr[1] != null && arr[1] !== '' && Number(arr[1]) < defMax));

  const hasSelectiveFilters =
    SELECTIVE_KEYS.some((k) => {
      const v = filters[k];
      return Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== '';
    }) ||
    rangeNarrowed(filters.power, 0, 1000) ||
    rangeNarrowed(filters.capacity, 0, 1000) ||
    rangeNarrowed(filters.price, 0, priceMaxDefault);
  const showRecommendedTop = !modelMode && pageIndex === 1 && !urlSearch && !hasSelectiveFilters && recommendedProducts.length > 0;
  const recIds = recommendedProducts.map((p) => p.id);
  const displayProducts = showRecommendedTop
    ? [...recommendedProducts, ...products.filter((p) => !recIds.includes(p.id))]
    : products;

  const activeTotalCount = modelMode ? modelTotalCount : totalCount;
  const activeTotalPages = modelMode ? modelTotalPages : totalPages;
  const activeIsLoading = modelMode ? isModelsLoading : isProductsLoading;
  const activeItems = modelMode ? models : displayProducts;
  const countLabel = t(modelMode ? 'licznik_modeli' : 'licznik_produktow', { count: activeTotalCount ?? 0 });

  const switchMode = (mode) => {
    if (mode === viewMode) return;
    setViewMode(mode);
    try { localStorage.setItem(viewKey, mode); } catch (e) { /* ignore */ }
    setSortOrder(mode === 'grouped' ? 'offers' : 'availability');
    onPageChange(null, 1);
  };

  const filtersSchema = productType === 'panel' ? panelFiltersSchema
    : productType === 'inwerter' ? inverterFiltersSchema
    : productType === 'magazyn' ? magazineFiltersSchema
    : productType === 'wallbox' ? wallboxFiltersSchema : null;

  return (
    <>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{t('filtrowanie')}</DialogTitle>
        <DialogContent><Filters filtersSchema={filtersSchema} onFilterChange={setFilters} productType={productType} initialFilters={initialApplied} modelMode={modelMode} /></DialogContent>
        <DialogActions><button onClick={handleClose} color='primary'>{t('zamknij')}</button></DialogActions>
      </Dialog>

      <div className='flex mt-10'>
        {/* Sidebar od xl (1280px), nie od 2xl (1536px) — typowe laptopy (1366/1440)
            wpadały w wersję z przyciskiem „Filtruj", mimo że miejsca starcza.
            Panel ma w-72 + ml-4 (~304px), więc przy 1280px zostaje ~910px na treść
            po zmniejszeniu bocznych marginesów poniżej 2xl. */}
        <div className='hidden xl:block'><Filters filtersSchema={filtersSchema} onFilterChange={setFilters} productType={productType} initialFilters={initialApplied} modelMode={modelMode} /></div>

        <div className='flex-1 px-4 lg:px-24 xl:px-10 2xl:px-24'>
          <div className="flex flex-col gap-4 mb-6 lg:flex-row lg:items-center lg:justify-between">
            <BreadcrumbsComponent />
            {/* Slot na wejście do asystenta doboru — kategorie, które go nie
                mają, po prostu nic tu nie przekazują. */}
            {akcja && <div className="flex justify-center">{akcja}</div>}
            <div className="flex flex-wrap items-center justify-center gap-2">
              {OKAZJE_DOSTEPNE && KATEGORIE_OKAZJI.includes(productType) && (
                <PrzyciskOkazji productType={productType} t={t} />
              )}
              <ViewSwitch modelMode={modelMode} onChange={switchMode} t={t} />
            </div>
            <div className="flex items-center gap-6 lg:justify-end">
              <button onClick={handleClickOpen} className="text-sm hover:text-gray-400 border border-gray-300 px-2 py-1 rounded-md group inline-flex justify-center xl:hidden">
                {t('filtruj')}<TuneIcon fontSize="small" className="-mr-1 ml-1 hover:text-gray-400" aria-hidden="true" />
              </button>
              <Sort onSortChange={setSortOrder} value={sortOrder} options={modelMode ? MODEL_SORT_OPTIONS : undefined} />
            </div>
          </div>

          {!activeIsLoading && activeTotalCount !== null && (
            <p className="mb-4 text-xs text-gray-500">{t('licznik_wstep')}{' '}<span className="font-semibold text-blue-600">{activeTotalCount}</span>{' '}{countLabel}</p>
          )}

          {urlSearch && (
            <div className="mb-4 flex items-center gap-2 flex-wrap text-sm">
              <span className="text-gray-500">{t('wyniki_dla')}</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-100 pl-3 pr-1.5 py-1 font-medium text-blue-700">
                <span className="truncate max-w-[240px]">{urlSearch}</span>
                <button onClick={() => { const params = new URLSearchParams(location.search); params.delete('search'); params.set('page', '1'); navigate({ search: params.toString() }); }} aria-label={t('wyczysc_wyszukiwanie')} className="flex items-center justify-center w-5 h-5 rounded-full text-blue-500 hover:bg-blue-100 transition-colors">
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              </span>
            </div>
          )}

          {activeIsLoading ? (
            <div className='flex justify-center items-center h-full py-20'><Loader /></div>
          ) : (
            activeItems.length === 0 ? (
              <div className='flex flex-col justify-center items-center h-full'>
                <FontAwesomeIcon icon={faBoxOpen} size='5x' className='text-gray-300 mb-4' />
                <p className='text-3xl font-semibold text-gray-300'>{t('brak_wynikow')}</p>
              </div>
            ) : (
              <ul className='mx-auto'>
                {modelMode
                  ? activeItems.map((m) => <ModelProduct key={`m-${m.id}`} model={m} productType={productType} />)
                  : activeItems.map((product) => (
                      <Product key={`${product.id}-${isRecommended(productType, product.id) ? 'rec' : ''}`} product={product} productType={productType} recommended={isRecommended(productType, product.id)} />
                    ))}
              </ul>
            )
          )}
        </div>
      </div>

      <Stack spacing={2} className='justify-center mt-10 flex items-center'>
        <Pagination count={activeTotalPages} page={pageIndex} onChange={onPageChange} color='primary' size='large' showFirstButton showLastButton />
      </Stack>
    </>
  );
};

export default ProductPage;
